import {
    Box,
    Chip,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import SuccessToast from "@/components/SuccessToast";
import { formatDateStandard, StyledTableRow } from "@/components/Table/DataTable.tsx";
import { useQueryOptionsFactory } from "@/queries";
import type { ListGateCodes } from "@/queries/gateCodes.ts";
import GateCodeForm from "@/routes/admin/-forms/GateCodeForm.tsx";

export const Route = createFileRoute("/admin/gateCodes/")({
    component: RouteComponent,
    pendingComponent: LinearProgress,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(context.qof.gateCodes.list());
    },
});

function RouteComponent() {
    const qof = useQueryOptionsFactory();
    const pageData: { data: ListGateCodes[] } = useSuspenseQuery(qof.gateCodes.list()).data;

    const [showSuccessToast, setShowSuccessToast] = useState(false);

    const getWeekColor = (week: string) => {
        switch (week) {
            case "week1":
                return { backgroundColor: "#dbeafe", color: "#1e40af" };
            case "week2":
                return { backgroundColor: "#dcfce7", color: "#166534" };
            case "week3":
                return { backgroundColor: "#fef3c7", color: "#92400e" };
            default:
                return { backgroundColor: "#f1f5f9", color: "#64748b" };
        }
    };

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
                <PageHeader
                    title="Gate Code Management"
                    instructions={[
                        "Set gate codes for Week 1, Week 2, and Week 3",
                        "Each week can have one active gate code at a time",
                        "Saving a code will update the existing code for that week",
                        "Set start and end dates to control when codes are active",
                    ]}
                />

                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <GateCodeForm
                        gateCodes={pageData.data}
                        setShowSuccessToast={setShowSuccessToast}
                    />
                </Box>
            </Paper>

            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box>
                    <Box
                        sx={{
                            p: 3,
                            backgroundColor: "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: "#1e293b",
                                fontWeight: 600,
                            }}
                        >
                            Current Gate Codes
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#64748b",
                                mt: 0.5,
                            }}
                        >
                            Gate codes for Week 1, Week 2, and Week 3
                        </Typography>
                    </Box>

                    <TableContainer sx={{ backgroundColor: "white" }}>
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        "& .MuiTableCell-head": {
                                            backgroundColor: "#f8fafc",
                                            borderBottom: "2px solid #e2e8f0",
                                            fontWeight: 600,
                                            color: "#1e293b",
                                        },
                                    }}
                                >
                                    <TableCell>Week</TableCell>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>End Date</TableCell>
                                    <TableCell>Gate Code</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {pageData.data.map((gateCode, index) => {
                                    return (
                                        <StyledTableRow
                                            key={gateCode.id}
                                            sx={{
                                                backgroundColor:
                                                    index % 2 === 0 ? "#ffffff" : "#f8fafc",
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={`Week ${gateCode.id}`}
                                                    size="small"
                                                    sx={{
                                                        ...getWeekColor(gateCode.periodId),
                                                        fontWeight: 500,
                                                        fontSize: "0.75rem",
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell sx={{ fontWeight: 500 }}>
                                                {gateCode.startDt
                                                    ? formatDateStandard(gateCode.startDt)
                                                    : "-"}
                                            </TableCell>

                                            <TableCell sx={{ fontWeight: 500 }}>
                                                {gateCode.endDt
                                                    ? formatDateStandard(gateCode.endDt)
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>
                                                {gateCode.code ? (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            fontFamily: "monospace",
                                                            backgroundColor: "#f1f5f9",
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            fontSize: "0.9rem",
                                                            fontWeight: 600,
                                                            color: "#1e293b",
                                                        }}
                                                    >
                                                        {gateCode.code}
                                                    </Box>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "#64748b",
                                                            fontStyle: "italic",
                                                        }}
                                                    >
                                                        Not configured
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            <SuccessToast
                open={showSuccessToast}
                onClose={() => setShowSuccessToast(false)}
                title="Gate Code Updated Successfully!"
                message="The gate code for the selected week has been updated."
            />
        </Box>
    );
}
